import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api } from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { garmentType, productColour, type Garment } from '@/lib/catalog';
import { conditionOptions, classifyListing, conditionPhrase, detectSize } from '@/lib/aiListing';
import type { Product } from '@/components/product/ProductCard';

/**
 * Souqli AI listing tool - heuristic ("smart autofill") draft generator.
 *
 * The seller describes the item in plain words (and optionally drops a few
 * photos); we derive brand, category, condition, colour, size, a clean title
 * and a polished description with rules - no external AI service or API key.
 * A suggested price is computed from comparable live listings. The finished
 * draft is handed to the normal /dashboard/sell flow for review + submission.
 */

type Condition = 'NEW_TAGS' | 'LIKE_NEW' | 'EXCELLENT' | 'GOOD' | 'FAIR';

interface Draft {
  category: string;
  brand: string;
  title: string;
  description: string;
  size: string;
  color: string;
  condition: Condition;
  priceGBP?: number;
  originalPriceGBP?: number;
}

interface PhotoFile { id: string; file: File; url: string }

const SELL_CATEGORIES = [
  { slug: 'womenswear', label: 'Womenswear' },
  { slug: 'menswear', label: 'Menswear' },
  { slug: 'shoes', label: 'Shoes' },
  { slug: 'bags', label: 'Bags' },
  { slug: 'electronics', label: 'Electronics' },
  { slug: 'watches', label: 'Watches' },
  { slug: 'kids', label: 'Kids' },
  { slug: 'fragrance', label: 'Fragrance' },
] as const;

// Known brands - first match wins. Kept broad across fashion + tech.
const BRANDS = [
  'Apple', 'iPhone', 'iPad', 'MacBook', 'AirPods', 'Samsung', 'Galaxy', 'Sony', 'Bose', 'Dyson',
  'Nintendo', 'PlayStation', 'Xbox', 'Google Pixel', 'Microsoft', 'Dell', 'HP', 'Lenovo', 'Canon', 'Nikon',
  'Nike', 'Adidas', 'New Balance', 'Puma', 'Reebok', 'Converse', 'Vans', 'Asics', 'Salomon', 'On Running',
  'Burberry', 'Gucci', 'Prada', 'Louis Vuitton', 'Loewe', 'Mulberry', 'Bottega Veneta', 'Saint Laurent',
  'Balenciaga', 'Hermes', 'Chanel', 'Dior', 'Fendi', 'Celine', 'Valentino', 'Versace', 'Givenchy',
  'Acne Studios', 'Max Mara', 'COS', 'Arket', 'Ganni', 'Toteme', 'Reiss', 'Whistles', 'Sandro', 'Maje',
  'Ralph Lauren', 'Tommy Hilfiger', 'Lacoste', 'Stone Island', 'Carhartt', 'The North Face', 'Patagonia',
  'Barbour', 'Levi', 'Wrangler', 'Uniqlo', 'Zara', 'Mango', 'H&M', 'ASOS', 'Massimo Dutti',
  'Rolex', 'Omega', 'Tag Heuer', 'Tudor', 'Seiko', 'Cartier', 'Casio', 'Tissot', 'Garmin',
  'Jo Malone', 'Tom Ford', 'Creed', 'Chloe', 'Marc Jacobs', 'YSL',
];

const u = (id: string) => `https://images.unsplash.com/photo-${id}?w=600&h=600&fit=crop&auto=format&q=80`;

/* ───────────────────────── Heuristics ───────────────────────── */

function detectBrand(t: string): string | undefined {
  const lower = t.toLowerCase();
  for (const b of BRANDS) {
    if (lower.includes(b.toLowerCase())) {
      // Normalise sub-brands to their house name where helpful.
      if (['iphone', 'ipad', 'macbook', 'airpods'].includes(b.toLowerCase())) return 'Apple';
      if (b.toLowerCase() === 'galaxy') return 'Samsung';
      if (b.toLowerCase() === 'google pixel') return 'Google';
      if (b.toLowerCase() === 'levi') return 'Levi’s';
      if (b.toLowerCase() === 'ysl') return 'Saint Laurent';
      return b;
    }
  }
  return undefined;
}

function detectPrice(t: string): number | undefined {
  const m = t.match(/£\s?(\d{1,5})(?:\.\d{1,2})?/);
  return m ? Number(m[1]) : undefined;
}

const TITLE_STOP = /\b(brand[- ]?new|bnwt|like[- ]?new|worn once|excellent|good condition|well[- ]?loved|used|pre[- ]?owned|pre[- ]?loved|second[- ]?hand|comes? with|includes?|smoke[- ]?free|pet[- ]?free|selling|for sale|barely (?:worn|used)|no (?:box|tags?))\b/gi;

function buildTitle(raw: string, brand?: string): string {
  // Use the first clause as the headline; strip selling/condition fluff.
  let core = raw.split(/[,.\n]/)[0] || raw;
  core = core.replace(TITLE_STOP, ' ').replace(/£\s?\d+(?:\.\d+)?/g, ' ').replace(/\s+/g, ' ').trim();
  // Title-case while keeping ALLCAPS model tokens (e.g. 256GB, M2).
  core = core
    .split(' ')
    .map((w) => (/[0-9]/.test(w) || w.length <= 2 ? w : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ')
    .trim();
  if (brand && !core.toLowerCase().includes(brand.toLowerCase().split(' ')[0])) {
    core = `${brand} ${core}`.trim();
  }
  if (!core) core = brand ?? 'Pre-loved item';
  return core.slice(0, 80);
}

function buildDescription(raw: string, d: { brand?: string; category?: string; garment: Garment; colour?: string; condition: Condition; size: string; sealed?: boolean; fillPercent?: number }): string {
  const lower = raw.toLowerCase();
  // Family-aware phrasing: a sealed phone reads "brand new and sealed", a watch
  // "pre-owned", a fragrance "around 80% full" - not a blanket "worn".
  const cond = conditionPhrase(d.condition, d.category, d.garment, { sealed: d.sealed, fillPercent: d.fillPercent });
  const garmentWord = d.garment === 'other' ? 'piece' : d.garment.replace('tshirt', 't-shirt');
  const lines: string[] = [];

  const lead = [d.brand, d.colour, garmentWord].filter(Boolean).join(' ');
  lines.push(`${lead ? lead.charAt(0).toUpperCase() + lead.slice(1) : 'A great find'} - ${cond.label}, ${cond.body}.`);

  if (d.size) lines.push(`Size / spec: ${d.size}.`);

  // Surface accessories / extras the seller mentioned.
  const extras: string[] = [];
  for (const [re, name] of [
    [/\bbox\b/, 'original box'], [/\btags?\b/, 'tags'], [/\breceipt\b/, 'receipt'],
    [/\bcable\b/, 'cable'], [/\bcharger\b/, 'charger'], [/\bcase\b/, 'case'],
    [/\bdust ?bag\b/, 'dust bag'], [/\bpapers?\b|certificate/, 'authenticity papers'],
  ] as [RegExp, string][]) {
    if (re.test(lower)) extras.push(name);
  }
  if (extras.length) lines.push(`Comes with ${extras.join(', ')}.`);

  // Surface any flaws honestly.
  const flaws: string[] = [];
  for (const [re, name] of [
    [/\bscratch/, 'a few light scratches'], [/\bscuff/, 'minor scuffs'],
    [/\bstain/, 'a small mark'], [/\bcrack/, 'a hairline crack'],
    [/\bfade|faded/, 'slight fading'], [/\btear|rip\b/, 'a small repair'],
  ] as [RegExp, string][]) {
    if (re.test(lower)) flaws.push(name);
  }
  if (flaws.length) lines.push(`Honest note: ${flaws.join(' and ')} - reflected in the price.`);

  lines.push('From a smoke-free home. Ships fast, with payment protected by Souqli.');
  return lines.join(' ');
}

const median = (nums: number[]): number | undefined => {
  if (!nums.length) return undefined;
  const s = [...nums].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
};

/* ───────────────────────── Component ───────────────────────── */

const SESSION_KEY = 'souqli_ai_draft';

export default function AiListing() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [raw, setRaw] = useState('');
  const [photos, setPhotos] = useState<PhotoFile[]>([]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [priceLow, setPriceLow] = useState<number | undefined>();
  const [priceHigh, setPriceHigh] = useState<number | undefined>();
  const [comps, setComps] = useState<number>(0);
  const fileRef = useRef<HTMLInputElement | null>(null);

  // Live catalogue for price comps (cached by react-query).
  const { data: catalogue = [] } = useQuery<Product[]>({
    queryKey: ['products', 'all'],
    queryFn: async () => (await api.get('/products')).data,
    retry: false,
    staleTime: 60_000,
  });

  useEffect(() => () => photos.forEach((p) => URL.revokeObjectURL(p.url)), [photos]);

  const addFiles = useCallback((files: FileList | File[]) => {
    const incoming = Array.from(files).filter((f) => f.type.startsWith('image/') && f.size <= 12 * 1024 * 1024);
    if (!incoming.length) return;
    setPhotos((prev) => [...prev, ...incoming.map((file) => ({ id: crypto.randomUUID(), file, url: URL.createObjectURL(file) }))].slice(0, 10));
  }, []);

  const examples = useMemo(() => [
    'iPhone 13 Pro 256GB graphite, used about a year, tiny scratch on the frame, comes with original box and cable. £450',
    'Acne Studios wool scarf in camel, worn twice, excellent condition, no flaws.',
    'Nike Air Max 90 trainers, UK 9, good condition with light scuffs on the sole.',
    'Burberry trench coat, women’s size M, beige, like new, dust bag included.',
  ], []);

  function generate() {
    const t = raw.trim();
    if (t.length < 12) { toast.error('Add a sentence or two describing your item first.'); return; }

    const brand = detectBrand(t);
    // One smart-agent pass: category, condition and product family together.
    const { category, garment, condition, sealed, fillPercent } = classifyListing(t);
    const colour = productColour({ title: t });
    const size = detectSize(t, garment);
    const typedPrice = detectPrice(t);

    // Price comps: same category, prefer same brand, then similar garment.
    const inCat = catalogue.filter((c) => (c as any).category === category);
    const sameBrand = brand ? inCat.filter((c) => (c.brand ?? '').toLowerCase().includes(brand.toLowerCase().split(' ')[0])) : [];
    const sameType = inCat.filter((c) => garmentType(c) === garment);
    const pool = (sameBrand.length >= 2 ? sameBrand : sameType.length >= 2 ? sameType : inCat).map((c) => c.priceGBP).filter((n) => n > 0);
    const mid = median(pool);
    setComps(sameBrand.length >= 2 ? sameBrand.length : sameType.length >= 2 ? sameType.length : inCat.length);

    let suggested = typedPrice;
    if (mid) {
      setPriceLow(Math.max(1, Math.round((mid * 0.85) / 5) * 5));
      setPriceHigh(Math.round((mid * 1.15) / 5) * 5);
      if (!suggested) suggested = Math.round(mid / 5) * 5;
    } else {
      setPriceLow(undefined); setPriceHigh(undefined);
    }

    setDraft({
      category,
      brand: brand ?? '',
      title: buildTitle(t, brand),
      description: buildDescription(t, { brand, category, garment, colour, condition, size, sealed, fillPercent }),
      size,
      color: colour ?? 'black',
      condition,
      priceGBP: suggested,
    });
    toast.success('Draft ready - review and tweak below.');
  }

  function field<K extends keyof Draft>(k: K, v: Draft[K]) {
    setDraft((d) => (d ? { ...d, [k]: v } : d));
  }

  function continueToSell() {
    if (!draft) return;
    if (!draft.priceGBP || draft.priceGBP < 1) { toast.error('Add an asking price to continue.'); return; }
    const prefill = { ...draft };
    // Stash text fields so they survive a login redirect (photos can't be serialised).
    try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(prefill)); } catch { /* ignore */ }
    if (!user) {
      toast('Log in to finish and publish your listing.', { icon: '🔐' });
      navigate('/login');
      return;
    }
    navigate('/dashboard/sell', { state: { prefill: { ...prefill, photos: photos.map((p) => p.file) } } });
  }

  return (
    <div className="bg-cream text-ink min-h-screen">
      {/* HERO */}
      <section className="section-dark relative text-cream py-16 md:py-20 overflow-hidden" style={{ background: 'linear-gradient(135deg,#0F0F12 0%,#1a1208 45%,#2c1e08 72%,#0F0F12 100%)' }}>
        <div className="absolute inset-0 dotgrid-light bg-[length:18px_18px] opacity-15 pointer-events-none" />
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 72% 50%, rgba(201,169,97,0.12) 0%, transparent 60%)' }} />
        <div className="relative max-w-[900px] mx-auto px-6 md:px-10 text-center">
          <span className="pill bg-gold text-ink text-[10px] mb-4 inline-block">✨ AI LISTING TOOL</span>
          <h1 className="display text-4xl md:text-5xl text-cream mb-4 leading-tight">Describe it.<br />We write the listing.</h1>
          <p className="text-cream/65 text-base md:text-lg max-w-[54ch] mx-auto leading-relaxed">
            Type a sentence about your item - brand, condition, what is included. Souqli detects the category,
            drafts a clean title and description, and suggests a fair price from comparable sales.
          </p>
        </div>
      </section>

      <div className="max-w-[1040px] mx-auto px-6 md:px-10 py-12 grid lg:grid-cols-[1fr_380px] gap-8 items-start">
        {/* INPUT */}
        <div className="space-y-5">
          <section className="bg-white border border-warmline rounded-luxe p-6 shadow-luxe">
            <div className="stamp text-gold-dark text-[9px]">01 · DESCRIBE YOUR ITEM</div>
            <h2 className="display text-2xl mt-1 mb-3">What are you selling?</h2>
            <textarea
              value={raw}
              onChange={(e) => setRaw(e.target.value)}
              className="input min-h-[140px]"
              placeholder="e.g. iPhone 13 Pro 256GB graphite, used a year, tiny scratch on the frame, comes with box and cable. £450"
              maxLength={600}
            />
            <div className="flex flex-wrap gap-2 mt-3">
              {examples.map((ex, i) => (
                <button key={i} type="button" onClick={() => setRaw(ex)} className="stamp text-[9px] bg-cream-deep hover:bg-gold/20 text-mute hover:text-gold-dark px-2.5 py-1 rounded transition text-left">
                  ▾ EXAMPLE {i + 1}
                </button>
              ))}
            </div>

            {/* optional photos */}
            <div className="mt-5">
              <div className="stamp text-gold-dark text-[9px] mb-2">PHOTOS · OPTIONAL</div>
              <div
                onClick={() => fileRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
                className="border-2 border-dashed border-warmline rounded-luxe p-4 text-center cursor-pointer hover:border-gold transition bg-cream-deep"
              >
                <div className="text-2xl">📸</div>
                <div className="text-[12px] text-mute mt-1">Drop photos to carry into your listing (optional)</div>
                <input ref={fileRef} type="file" multiple accept="image/*" className="hidden" onChange={(e) => e.target.files && addFiles(e.target.files)} />
              </div>
              {photos.length > 0 && (
                <div className="grid grid-cols-6 gap-2 mt-3">
                  {photos.map((p) => (
                    <div key={p.id} className="aspect-square rounded notched-sm overflow-hidden border border-warmline relative">
                      <img src={p.url} alt="" className="w-full h-full object-cover" />
                      <button onClick={() => setPhotos((prev) => prev.filter((x) => x.id !== p.id))} className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-ink/80 text-cream text-[9px] grid place-items-center">×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button onClick={generate} className="btn btn-gold w-full mt-5">✨ Generate listing</button>
          </section>

          {/* DRAFT (editable) */}
          {draft && (
            <section className="bg-white border border-gold/40 rounded-luxe p-6 shadow-frame">
              <div className="stamp text-gold-dark text-[9px]">02 · YOUR DRAFT</div>
              <h2 className="display text-2xl mt-1 mb-4">Review &amp; adjust</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="label">Title</label>
                  <input className="input" value={draft.title} maxLength={80} onChange={(e) => field('title', e.target.value)} />
                </div>
                <div>
                  <label className="label">Brand</label>
                  <input className="input" value={draft.brand} onChange={(e) => field('brand', e.target.value)} placeholder="Unbranded" />
                </div>
                <div>
                  <label className="label">Category</label>
                  <select className="input" value={draft.category} onChange={(e) => field('category', e.target.value)}>
                    {SELL_CATEGORIES.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Condition</label>
                  <select className="input" value={draft.condition} onChange={(e) => field('condition', e.target.value as Condition)}>
                    {conditionOptions(draft.category, garmentType({ title: draft.title, brand: draft.brand, category: draft.category })).map((c) => <option key={c.v} value={c.v}>{c.label} - {c.body}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Size / spec</label>
                  <input className="input" value={draft.size} onChange={(e) => field('size', e.target.value)} placeholder="UK 10 · 256GB · M" />
                </div>
                <div className="sm:col-span-2">
                  <label className="label">Description</label>
                  <textarea className="input min-h-[120px]" value={draft.description} maxLength={500} onChange={(e) => field('description', e.target.value)} />
                </div>
                <div>
                  <label className="label">Asking price (£)</label>
                  <div className="relative">
                    <span className="absolute left-4 top-3 text-mute display text-base">£</span>
                    <input className="input pl-8 text-lg" type="number" min={1} value={draft.priceGBP ?? ''} onChange={(e) => field('priceGBP', e.target.value ? Number(e.target.value) : undefined)} placeholder="0" />
                  </div>
                  {priceLow && priceHigh && (
                    <p className="stamp text-mute text-[9px] mt-1.5">⌖ SOUQLI SUGGESTS £{priceLow}-£{priceHigh}{comps ? ` · ${comps} COMPARABLE` : ''}</p>
                  )}
                </div>
                <div>
                  <label className="label">RRP / original (£) <span className="text-mute font-normal lowercase">- optional</span></label>
                  <div className="relative">
                    <span className="absolute left-4 top-3 text-mute display text-base">£</span>
                    <input className="input pl-8 text-lg" type="number" min={1} value={draft.originalPriceGBP ?? ''} onChange={(e) => field('originalPriceGBP', e.target.value ? Number(e.target.value) : undefined)} placeholder="-" />
                  </div>
                </div>
              </div>
              <button onClick={continueToSell} className="btn btn-primary w-full mt-6">Continue to listing →</button>
              <p className="stamp text-mute text-[9px] text-center mt-3">YOU REVIEW EVERYTHING BEFORE IT GOES LIVE · VERIFIED BY SOUQLI</p>
            </section>
          )}
        </div>

        {/* SIDEBAR */}
        <aside className="space-y-5 lg:sticky lg:top-6">
          <div className="bg-white border border-warmline rounded-luxe p-6">
            <div className="stamp text-gold-dark text-[9px] mb-2">HOW IT WORKS</div>
            <ol className="space-y-3 text-sm">
              {[
                ['Describe', 'One or two sentences about your item - the more detail, the better the draft.'],
                ['Generate', 'We detect the category, brand, condition and colour, then write a clean title + description.'],
                ['Price', 'A fair range is suggested from comparable live listings.'],
                ['Review', 'Edit anything, then continue to the full lister to add photos and submit.'],
              ].map(([t, b], i) => (
                <li key={t} className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-gold text-ink grid place-items-center display text-sm shrink-0">{i + 1}</span>
                  <div><span className="font-medium">{t}.</span> <span className="text-mute">{b}</span></div>
                </li>
              ))}
            </ol>
          </div>
          <div className="rounded-luxe overflow-hidden border border-warmline">
            <img src={u('1441986300917-64674bd600d8')} alt="" className="w-full h-32 object-cover" />
            <div className="p-5 bg-white">
              <div className="display text-lg">List 20 items in 5 minutes</div>
              <p className="text-mute text-sm mt-1">Describe each piece, generate, review, done. No staring at empty forms.</p>
              <Link to="/dashboard/sell" className="text-gold-dark text-sm hover:underline mt-3 inline-block">Prefer the full form? →</Link>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
